import React from "react";
import { ProdRow, emptyProdRow } from "../components/OSProductivityTable";
import { SubprocessOption } from "../select/SubprocessCells";
import { useMachineOptions } from "../select/MachineSelect";
import { RAZMER_STAGE_ID, useRazmerProcessDefinition } from "../components/OSStageTree";
import { RNMachineSeed, deriveRNTotalProductivityRows, machineSeedsForStage } from "../utils/rnTotalProductivity";
import { isPerSubprocessStage, unmatchedActualRows } from "../utils/logbookStage";

interface UseRNProductivityPlanArgs {
  isRazmerStage: boolean;
  activeStage: string;
  activeSubprocess: SubprocessOption | undefined;
  subprocesses: SubprocessOption[];
  osProductivity: ProdRow[];
  productivityRows: ProdRow[];
  selectedSubprocessIds: string[];
  // Frozen machine seed of the RN snapshot; undefined = legacy RN (live machine codebook).
  machineSeeds?: RNMachineSeed[];
}

// Продуктивност — machine-id resolution (Размерување vs. active subprocess), OS-planned
// row seeding, and the merged actual/planned rows shown per stage + the "Вкупна
// продуктивност" rollup. Move-only out of RNWorkOrder.tsx — same memo/callback deps,
// same behavior.
export const useRNProductivityPlan = ({
  isRazmerStage,
  activeStage,
  activeSubprocess,
  subprocesses,
  osProductivity,
  productivityRows,
  selectedSubprocessIds,
  machineSeeds,
}: UseRNProductivityPlanArgs) => {
  const machineOptions = useMachineOptions();
  const machineById = React.useMemo(() => {
    const map = new Map<string, (typeof machineOptions)[number]>();
    machineOptions.forEach((machine) => map.set(machine._id, machine));
    return map;
  }, [machineOptions]);
  // Identity comes from the codebook (the active process definition with zero
  // subprocesses), never from name-matching its label — see OSStageTree.tsx.
  const razmerProcessDefinition = useRazmerProcessDefinition();
  const razmerProcessMachineIds = React.useMemo(() => {
    if (!razmerProcessDefinition?.machines) return [];
    const seen = new Set<string>();
    return [...razmerProcessDefinition.machines]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((row) => (row.machine && typeof row.machine === "object") ? row.machine._id : row.machine)
      .filter((id): id is string => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [razmerProcessDefinition]);
  const activeSubprocessMachineIds = React.useMemo(() => {
    if (!activeSubprocess?.machines) return [];
    const seen = new Set<string>();
    return [...activeSubprocess.machines]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((row) => (row.machine && typeof row.machine === "object") ? row.machine._id : row.machine)
      .filter((id): id is string => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [activeSubprocess]);
  const activeStageMachineIds = isRazmerStage ? razmerProcessMachineIds : activeSubprocessMachineIds;
  const defaultProductivityRow = React.useCallback((machineId: string, subprocessId: string): ProdRow => {
    const machine = machineSeeds
      ? machineSeedsForStage(machineSeeds, subprocessId).find((seed) => seed.machine === machineId)
      : machineById.get(machineId);
    const asStr = (value: number | null | undefined) => value === null || value === undefined ? "" : String(value);
    return {
      ...emptyProdRow(),
      machineId,
      montageTime: asStr(machine?.setupTime ?? null),
      dismountCleanTime: asStr(machine?.cleaningDismountTime ?? null),
      operatorCount: asStr(subprocesses.find((sp) => sp._id === subprocessId)?.operatorCount ?? null),
      subprocessId,
    };
  }, [machineById, machineSeeds, subprocesses]);

  const mergeProdActual = React.useCallback((os: ProdRow, saved?: ProdRow): ProdRow => ({
    ...emptyProdRow(),
    ...os,
    // `os` carries the OS-planned `setupParams` (read-only on RN); the operator's own
    // additions live separately in `extraSetupParams`, RN-owned, so they come from `saved`.
    extraSetupParams: saved?.extraSetupParams ?? [],
    montageStart: saved?.montageStart ?? "",
    montageEnd: saved?.montageEnd ?? "",
    machineStart: saved?.machineStart ?? "",
    machineEnd: saved?.machineEnd ?? "",
    dismountStart: saved?.dismountStart ?? "",
    dismountEnd: saved?.dismountEnd ?? "",
    cleaningStart: saved?.cleaningStart ?? "",
    cleaningEnd: saved?.cleaningEnd ?? "",
  }), []);

  // Same machine-id resolution as activeSubprocessMachineIds above, but for an arbitrary
  // stageId (not just activeSubprocess) so planProdRowsForStage can seed rows for every
  // selected subprocess, not only the one currently open.
  const machineIdsForStage = React.useCallback((stageId: string): string[] => {
    if (machineSeeds) return machineSeedsForStage(machineSeeds, stageId).map((seed) => seed.machine);
    if (stageId === RAZMER_STAGE_ID) return razmerProcessMachineIds;
    const subprocess = subprocesses.find((sp) => sp._id === stageId);
    if (!subprocess?.machines) return [];
    const seen = new Set<string>();
    return [...subprocess.machines]
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      .map((row) => (row.machine && typeof row.machine === "object") ? row.machine._id : row.machine)
      .filter((id): id is string => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [razmerProcessMachineIds, subprocesses, machineSeeds]);

  // Rows are driven by the OS master recipe, same as /os: one row per snapshot machine
  // PLUS a seeded default row for every machine configured on the stage's subprocess that
  // does not already have a saved row. Without seeding, a stage with no persisted
  // ingredient.productivity row for it renders empty even though machines are configured.
  const planProdRowsForStage = React.useCallback((stageId: string): ProdRow[] => {
    const fromOs = osProductivity.filter((p) => p.subprocessId === stageId);
    // Log Book по подпроцес: the one stage row already carries every machine's times.
    if (isPerSubprocessStage(fromOs, stageId)) return fromOs;
    const existing = new Set(fromOs.map((r) => r.machineId).filter(Boolean));
    const seeded = machineIdsForStage(stageId)
      .filter((id) => !existing.has(id))
      .map((id) => defaultProductivityRow(id, stageId));
    return [...fromOs, ...seeded];
  }, [osProductivity, machineIdsForStage, defaultProductivityRow]);

  const visibleProdRows = React.useMemo(() => {
    if (!activeStage) return [];
    const plan = planProdRowsForStage(activeStage);
    const rows = plan.length > 0 ? plan : [{ ...emptyProdRow(), subprocessId: activeStage }];
    const merged = rows.map((os) => mergeProdActual(
      { ...os, subprocessId: activeStage },
      productivityRows.find((r) => r.subprocessId === activeStage && r.machineId === os.machineId)
    ));
    if (!isPerSubprocessStage(plan, activeStage)) return merged;
    return [...merged, ...unmatchedActualRows(activeStage, plan, productivityRows, mergeProdActual, emptyProdRow)];
  }, [activeStage, planProdRowsForStage, productivityRows, mergeProdActual]);

  const totalProductivityRows = React.useMemo(() => {
    return deriveRNTotalProductivityRows({
      osProductivity,
      productivityRows,
      selectedSubprocessIds,
      subprocesses,
      razmerProcessMachineIds,
      machineOptions,
      machineSeeds,
    });
  }, [osProductivity, productivityRows, selectedSubprocessIds, subprocesses, razmerProcessMachineIds, machineOptions, machineSeeds]);

  return {
    machineOptions,
    machineById,
    razmerProcessMachineIds,
    activeSubprocessMachineIds,
    activeStageMachineIds,
    visibleProdRows,
    totalProductivityRows,
  };
};
